import { LocalDate, LocalDateTime, LocalTime, nativeJs } from '@js-joda/core';
import { compact } from 'lodash';
import { useMemo } from 'react';

import { AppointmentStatus, AvailabilitySimple } from '@dreryk/egabinet-appointment-frontend-api';
import { TimeSlot } from '@dreryk/time-slot-common-api';

import {
   TargetPosition,
   TimetableDataAccessor,
   TimetableDateRange,
   TimetableEvent,
   TimetableEventBackgroundConfiguration,
   TimetableEventFilter,
   TimetableSlotSelectionEvent,
} from '@dreryk/react-calendar';
import { assertUnreachable } from '@dreryk/react-common';

import { VisitSettlementStatusResolver } from '~libs.shared.visit-settlement';
import { convertLocalDateTimeToDate } from '~libs.typescript';

import { AppointmentEventSlotData, AppointmentHoverSlotData, AppointmentResource } from './AppointmentsTimetable.types';

export const getEventMaxStack = (): number => {
   return 2;
};

export const eventBackgroundConfiguration: TimetableEventBackgroundConfiguration<
   AppointmentEventSlotData,
   'primaryLighter' | 'borderLighter' | 'errorLighter' | 'successLighter'
> = {
   backgroundColors: ['primaryLighter', 'borderLighter', 'errorLighter', 'successLighter'],
   backgroundColorSelector: (event) => {
      if (!event.data.visit) {
         switch (event.data.status) {
            case 'REGISTERED':
               return event.data.timeSlotConfirmed ? 'primaryLighter' : 'successLighter';
            case 'CANCELLED':
               return 'errorLighter';
            default:
               return 'borderLighter';
         }
      }

      switch (event.data.visit.status) {
         case undefined:
         case 'IN_PROGRESS':
            return 'primaryLighter';
         case 'FINISHED_APPROVED':
         case 'FINISHED_NOT_APPROVED':
            return 'borderLighter';
         case 'CANCELLED':
            return 'errorLighter';
         default:
            return assertUnreachable(event.data.visit.status);
      }
   },
};

const SELECTION_END_TIME = '23:59:59';

const isMidnightNextDay = (start: Date, end: Date): boolean =>
   LocalDate.from(nativeJs(start))
      .plusDays(1)
      .atStartOfDay()
      .isEqual(LocalDateTime.from(nativeJs(end)));

const selectionOnlyWithinOneDay = (selection: TimetableDateRange): boolean => {
   const { start, end } = selection;

   if (isMidnightNextDay(start, end)) return true;

   return (
      start.getFullYear() === end.getFullYear() &&
      start.getMonth() === end.getMonth() &&
      start.getDay() === end.getDay() &&
      !LocalTime.from(nativeJs(end)).isAfter(LocalTime.parse(SELECTION_END_TIME))
   );
};

export const allowsSelectionOnlyWithinOneDate = (slot: TimetableSlotSelectionEvent<AppointmentResource>): boolean => {
   return selectionOnlyWithinOneDay(slot);
};

export const disallowSelection = (): boolean => {
   return false;
};

export const allowedEventUpdateSelectorOnlyWithinOneDateForSamePractitioner = (
   event: TimetableEvent<AppointmentEventSlotData>,
   targetPosition: TargetPosition
): boolean => {
   return selectionOnlyWithinOneDay(targetPosition) && event.resourceId === targetPosition.resourceId.toString();
};

export const filterCancelledAppointmentOrVisit: TimetableEventFilter<AppointmentEventSlotData> = (event) => {
   return event.data.status !== 'CANCELLED' && event.data.visit?.status !== 'CANCELLED';
};

export const withoutFilter: TimetableEventFilter<AppointmentEventSlotData> = () => {
   return true;
};

export const areSlotsDatesEqual = (first: AppointmentHoverSlotData, second: AppointmentHoverSlotData): boolean =>
   first.timeSlot.getBeginDate().getTime() === second.timeSlot.getBeginDate().getTime() &&
   first.timeSlot.getEndDate().getTime() === second.timeSlot.getEndDate().getTime();

export const getEventOrder = (appointmentStatus: AppointmentStatus): number => {
   return appointmentStatus === 'CANCELLED' ? 1 : -1;
};

export const isOverlappable = (appointmentStatus: AppointmentStatus): boolean => {
   return appointmentStatus === 'CANCELLED';
};

export const createTimeSlotFromAvailability = (availability: AvailabilitySimple): TimeSlot =>
   TimeSlot.of(
      convertLocalDateTimeToDate(availability.availabilityDate.atTime(availability.startTime)),
      convertLocalDateTimeToDate(availability.availabilityDate.atTime(availability.endTime))
   );

export const VisitSettlementCalendarDataAccessor: TimetableDataAccessor<AppointmentEventSlotData> = (props) => {
   const appointmentIds = useMemo<number[]>(() => {
      return compact(props.events.map((event) => event.data.appointmentId));
   }, [props.events]);

   return <VisitSettlementStatusResolver appointmentIds={appointmentIds} />;
};
